import { useTranslation } from 'react-i18next';

import { useConfig } from '@entities/config/api';
import { useConsent } from '@features/consent';
import { Button, Skeleton } from '@shared/ui';

import { LegalArticle } from './LegalArticle';
import styles from './LegalArticle.module.css';

/**
 * Cookie-Richtlinie — § 25 TDDDG.
 *
 * Таблица перечисляет **фактически** установленные cookie, а не типовой
 * список. Придумывать имена заранее нельзя: страница, обещающая аналитику,
 * которой нет, ничем не лучше страницы, умалчивающей об аналитике, которая
 * есть.
 *
 * Кнопка настроек показывается, только когда есть что настраивать: при пустом
 * `optional_cookie_categories` выбора не существует, и предлагать его —
 * имитация согласия. Это же правило прячет баннер.
 */
export function CookiesPage() {
  const { t } = useTranslation();
  const { data, isPending } = useConfig();
  const consent = useConsent();

  if (isPending || !data) {
    return (
      <LegalArticle title="Cookie-Richtlinie">
        <Skeleton height="18rem" radius="var(--radius-card)" />
      </LegalArticle>
    );
  }

  const optional = data.legal.optional_cookie_categories;

  return (
    <LegalArticle
      title="Cookie-Richtlinie"
      version={data.legal.document_versions.cookies}
      lead={<p>{t('legal.cookies.lead')}</p>}
    >
      <h2>Technisch notwendige Technologien</h2>
      <p>
        Diese Cookies sind erforderlich, damit der von Ihnen ausdrücklich gewünschte Dienst
        funktioniert. Sie werden nach § 25 Abs. 2 Nr. 2 TDDDG ohne Einwilligung gesetzt und lassen
        sich nicht abschalten, ohne den Dienst unbrauchbar zu machen. Ein Profil über Ihr Verhalten
        entsteht dabei nicht.
      </p>
      <div className={styles.tableWrap}>
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Anbieter</th>
              <th>Zweck</th>
              <th>Speicherdauer</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>__Host-carviq_session</td>
              <td>CarViQ.DE</td>
              <td>
                Hält Sie nach der Anmeldung eingeloggt. Enthält eine zufällige Kennung, keine
                personenbezogenen Inhalte.
              </td>
              <td>30 Tage</td>
            </tr>
            <tr>
              <td>__Host-carviq_csrf</td>
              <td>CarViQ.DE</td>
              <td>Schützt Formulare vor Anfragen fremder Websites (Cross-Site Request Forgery).</td>
              <td>Sitzung</td>
            </tr>
            <tr>
              <td>__Host-carviq_guest</td>
              <td>CarViQ.DE</td>
              <td>
                Verknüpft Ihre Anfrage mit dem Gerät, solange Sie ohne Konto arbeiten, damit Dialog
                und Ergebnisse erhalten bleiben.
              </td>
              <td>{data.guest.session_ttl_days} Tage</td>
            </tr>
            <tr>
              <td>carviq.hero-prompt · Speicher des Browsers</td>
              <td>CarViQ.DE</td>
              <td>
                Bewahrt Ihre begonnene Eingabe und die zuletzt gewählte Sprache. Verlässt Ihren
                Browser nicht.
              </td>
              <td>bis zum Schließen des Tabs bzw. bis zur Änderung</td>
            </tr>
          </tbody>
        </table>
      </div>

      <h2>Optionale Technologien</h2>
      {optional.length === 0 ? (
        <>
          <p>
            CarViQ.DE setzt derzeit <strong>keine</strong> Analyse-, Marketing- oder sonstigen
            optionalen Technologien ein. Es werden keine Daten an Analyse- oder Werbenetzwerke
            übermittelt, und es findet keine Übermittlung in Drittländer zu diesen Zwecken statt.
          </p>
          <p>
            Aus diesem Grund erscheint auch kein Einwilligungsbanner: Es gäbe nichts, worin Sie
            einwilligen könnten. Sobald sich das ändert, werden die betreffenden Technologien hier
            mit Anbieter, Zweck, Speicherdauer und gegebenenfalls Drittlandübermittlung aufgeführt
            und erst nach Ihrer Einwilligung geladen.
          </p>
        </>
      ) : (
        <>
          <p>
            Die folgenden Kategorien werden erst geladen, nachdem Sie eingewilligt haben. Ihre
            Entscheidung können Sie jederzeit mit Wirkung für die Zukunft ändern.
          </p>
          <ul>
            {optional.map((category) => (
              <li key={category}>
                <strong>{t(`consent.category.${category}.title`)}</strong> —{' '}
                {t(`consent.category.${category}.text`)}
              </li>
            ))}
          </ul>
          <p>
            <Button
              variant="secondary"
              onClick={() => {
                consent.openSettings();
              }}
            >
              {t('consent.settings')}
            </Button>
          </p>
        </>
      )}

      <h2>Cookies im Browser löschen</h2>
      <p>
        Unabhängig von den Einstellungen auf dieser Website können Sie gesetzte Cookies jederzeit in
        Ihrem Browser löschen und deren Speicherung einschränken. Werden die technisch notwendigen
        Cookies blockiert, sind Anmeldung und Auftragsbearbeitung nicht mehr möglich.
      </p>
    </LegalArticle>
  );
}
